import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { attendanceApi, leavesApi, holidaysApi, leavePoliciesApi, usersApi } from '../../services/api';
import useStore from '../../store';
import './Attendance.css';

const STATUS_OPTIONS = [
  { value: 'working',     label: 'Working',     color: '#10B981', bg: '#D1FAE5', icon: '💼' },
  { value: 'half_day',    label: 'Half Day',    color: '#F59E0B', bg: '#FEF3C7', icon: '⚡' },
  { value: 'not_working', label: 'Not Working', color: '#EF4444', bg: '#FEE2E2', icon: '🏠' },
];

const ATTENDANCE_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const ATTENDANCE_TIME_MIN = '08:00';
const ATTENDANCE_TIME_MAX = '22:00';

const formatDatePart = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const getPart = (type) => parts.find((part) => part.type === type)?.value || '00';
  return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
};

const formatTimePart = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const getPart = (type) => parts.find((part) => part.type === type)?.value || '00';
  return `${getPart('hour')}:${getPart('minute')}`;
};

const isTimeInAllowedRange = (value) => {
  if (!value) return true;
  return value >= ATTENDANCE_TIME_MIN && value <= ATTENDANCE_TIME_MAX;
};

const isNowWithinAttendanceWindow = () => {
  const currentTime = formatTimePart(new Date(), ATTENDANCE_TIME_ZONE);
  return currentTime >= ATTENDANCE_TIME_MIN && currentTime <= ATTENDANCE_TIME_MAX;
};

const getDayLabel = (dateStr) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) {
    return '—';
  }

  return d.toLocaleDateString('en-IN', {
    timeZone: ATTENDANCE_TIME_ZONE,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
};

const formatTeamAttendanceDate = (dateStr, fallbackDate) => {
  const value = getDayLabel(dateStr);
  if (value !== '—') {
    return value;
  }

  return getDayLabel(fallbackDate);
};

const isDateWithinRange = (dateStr, startDate, endDate) =>
  dateStr >= startDate && dateStr <= endDate;

export default function AttendancePage({ teamView = false }) {
  const today = formatDatePart(new Date(), ATTENDANCE_TIME_ZONE);
  const { user } = useStore();
  const isAdmin = user?.role === 'admin';
  const isTeamView = teamView && ['admin', 'manager'].includes(user?.role);

  const [todayRecord, setTodayRecord] = useState(null);
  const [formStatus, setFormStatus] = useState('working');
  const [formDesc, setFormDesc] = useState('');
  const [formCheckIn, setFormCheckIn] = useState('09:00');
  const [formCheckOut, setFormCheckOut] = useState('');
  const [saving, setSaving] = useState(false);

  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [calMonth, setCalMonth] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [teamDate, setTeamDate] = useState(today);
  const [markStatusFilter, setMarkStatusFilter] = useState('all');
  const [allTeamUsers, setAllTeamUsers] = useState([]);
  const [leaves, setLeaves] = useState([]); // Approved leaves for calendar
  const [holidays, setHolidays] = useState([]); // Holidays for calendar
  const [policy, setPolicy] = useState(null);
  const [teamFilter, setTeamFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [teamSearch, setTeamSearch] = useState('');
  const [markModal, setMarkModal] = useState(null); // { user, date, status, description, checkIn, checkOut, existingRecord }
  const [markSaving, setMarkSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const startDate = `${calMonth.year}-${String(calMonth.month).padStart(2,'0')}-01`;
      const endDate = `${calMonth.year}-${String(calMonth.month).padStart(2,'0')}-${String(new Date(calMonth.year, calMonth.month, 0).getDate()).padStart(2,'0')}`;
      if (isTeamView) {
        const [listRes, usersRes] = await Promise.all([
          attendanceApi.list({
            startDate: teamDate,
            endDate: teamDate,
            limit: 200,
          }),
          isAdmin ? usersApi.list({ limit: 1000 }) : usersApi.getReportees(user?.id)
        ]);
        setRecords(listRes.data.data || []);
        
        let fetchedUsers = usersRes.data.data || [];
        // listUsers API returns { count, rows } while getReportees might just return array
        const rows = Array.isArray(fetchedUsers) ? fetchedUsers : (fetchedUsers.rows || []);
        setAllTeamUsers(rows);

        setSummary(null);
        setTodayRecord(null);
        setLeaves([]);
        setHolidays([]);
        setPolicy(null);
        return;
      }

        const [todayRes, listRes, sumRes, leavesRes, holsRes] = await Promise.all([
        attendanceApi.byDate(today),
          attendanceApi.list({ startDate, endDate, limit: 31, userId: user?.id }),
        attendanceApi.summary({ year: calMonth.year, month: calMonth.month }),
          leavesApi.list({ status: 'approved', startDate, endDate, userId: user?.id }),
        holidaysApi.list({ year: calMonth.year }),
      ]);

      const rec = todayRes.data.data;
      setTodayRecord(rec);
      if (rec) {
        setFormStatus(rec.status);
        setFormDesc(rec.work_description || '');
        setFormCheckIn(rec.check_in || '09:00');
        setFormCheckOut(rec.check_out || '');
      }
      setRecords(listRes.data.data);
      setSummary(sumRes.data.data);

      // Filter leaves for this month and flatten to per-day
        const leavesList = (leavesRes.data.data || []).filter(
          (l) => l.status === 'approved' && (!user?.id || l.user_id === user.id)
        );
      setLeaves(leavesList);

      // Filter holidays for this month
      const holsList = (holsRes.data.data || []).filter(h => {
        const d = new Date(h.date);
        return d.getFullYear() === calMonth.year && d.getMonth() + 1 === calMonth.month;
      });

      let policyData = null;
      if (!isAdmin && user?.leave_policy_id) {
        try {
          const policyRes = await leavePoliciesApi.get(user.leave_policy_id);
          policyData = policyRes.data.data || null;
        } catch {
          policyData = null;
        }
      }

      setPolicy(policyData);
      const policyHolidays = (policyData?.holidays || []).filter(h => {
        const d = new Date(h.date);
        return d.getFullYear() === calMonth.year && d.getMonth() + 1 === calMonth.month;
      });

      setHolidays(isAdmin ? holsList : (policyData ? policyHolidays : holsList));
    } catch (err) {
      toast.error('Failed to load attendance data');
    }
  }, [today, calMonth, isAdmin, isTeamView, user?.leave_policy_id, teamDate, user?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async (e) => {
    e.preventDefault();

    if (!ATTENDANCE_TIME_ZONE) {
      toast.error('Unable to detect your timezone. Please check your browser settings.');
      return;
    }

    if (!isNowWithinAttendanceWindow()) {
      toast.error('Attendance can only be marked between 08:00 and 22:00');
      return;
    }

    if (!isTimeInAllowedRange(formCheckIn) || !isTimeInAllowedRange(formCheckOut)) {
      toast.error('Attendance time must be between 08:00 and 22:00');
      return;
    }

    setSaving(true);
    try {
      const leaveRes = await leavesApi.list({ userId: user?.id });
      const leaveList = leaveRes.data.data || [];
      const hasActiveLeave = leaveList.some((leave) =>
        leave.status === 'approved' &&
        isDateWithinRange(today, leave.start_date, leave.end_date)
      );

      if (hasActiveLeave) {
        toast.error('You already have a leave for today. Cancel the leave before logging attendance.');
        return;
      }

      await attendanceApi.upsert({
        date: today,
        status: formStatus,
        work_description: formDesc,
        check_in: formCheckIn || null,
        check_out: formCheckOut || null,
        time_zone: ATTENDANCE_TIME_ZONE,
      });
      toast.success(todayRecord ? 'Attendance updated!' : 'Attendance logged!');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  // Opens the admin/manager "mark attendance for this employee" modal,
  // pre-filling from their existing record for the selected team date (if any).
  const openMarkModal = (record) => {
    setMarkModal({
      user: record.user,
      date: record.date,
      status: record.isMissing ? 'working' : record.status,
      description: record.work_description || '',
      checkIn: record.check_in || '09:00',
      checkOut: record.check_out || '',
    });
  };

  const submitMarkModal = async (e) => {
    e.preventDefault();
    if (!markModal) return;

    if (!isTimeInAllowedRange(markModal.checkIn) || !isTimeInAllowedRange(markModal.checkOut)) {
      toast.error('Attendance time must be between 08:00 and 22:00');
      return;
    }

    setMarkSaving(true);
    try {
      await attendanceApi.upsert({
        user_id: markModal.user.id,
        date: markModal.date,
        status: markModal.status,
        work_description: markModal.description,
        check_in: markModal.checkIn || null,
        check_out: markModal.checkOut || null,
        time_zone: ATTENDANCE_TIME_ZONE,
      });
      toast.success(`Attendance saved for ${markModal.user.first_name}`);
      setMarkModal(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save attendance');
    } finally {
      setMarkSaving(false);
    }
  };

  // Build calendar grid with attendance, leave, and holiday info
  const buildCalendar = () => {
    const { year, month } = calMonth;
    const firstDay = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const recMap = {};
    records.forEach((r) => { recMap[r.date] = r; });

    // Map leaves by date
    const leaveMap = {};
    leaves.forEach((l) => {
      let d1 = new Date(l.start_date), d2 = new Date(l.end_date);
      for (let d = new Date(d1); d <= d2; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        leaveMap[dateStr] = l;
      }
    });

    // Map holidays by date
    const holMap = {};
    holidays.forEach((h) => { holMap[h.date] = h; });

    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      cells.push({
        day: d,
        dateStr,
        record: recMap[dateStr] || null,
        leave: leaveMap[dateStr] || null,
        holiday: holMap[dateStr] || null,
      });
    }
    return cells;
  };

  const monthName = new Date(calMonth.year, calMonth.month - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  const calCells = isTeamView ? [] : buildCalendar();

  const prevMonth = () => setCalMonth(({ year, month }) =>
    month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 }
  );
  const nextMonth = () => setCalMonth(({ year, month }) =>
    month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 }
  );

  const statusStyle = (status) => {
    const s = STATUS_OPTIONS.find((o) => o.value === status);
    return s ? { background: s.bg, color: s.color } : {};
  };

  const teamUsers = isTeamView
    ? Array.from(
        new Map(
          allTeamUsers
            .map((u) => [String(u.id), `${u.first_name || ''} ${u.last_name || ''}`.trim()])
        ).entries()
      )
    : [];
  const teamCompanies = isTeamView
    ? Array.from(new Set(allTeamUsers.map((u) => u.company).filter(Boolean)))
    : [];
  // Records scoped by every filter except status — used both to render the
  // table and to compute accurate per-tab counts for the status pills below.
  const recordsBeforeStatusFilter = isTeamView
    ? allTeamUsers
        .map((user) => {
          const record = records.find((item) => String(item.user_id) === String(user.id));
          if (record) {
            return { ...record, user, isMissing: false };
          }

          return {
            id: `missing-${user.id}-${teamDate}`,
            user,
            date: teamDate,
            status: 'not_marked',
            isMissing: true,
            check_in: null,
            check_out: null,
            work_description: null,
          };
        })
        .filter((r) => (companyFilter === 'all' ? true : r.user?.company === companyFilter))
        .filter((r) => (teamFilter === 'all' ? true : String(r.user?.id) === teamFilter))
        .filter((r) => {
          const q = teamSearch.trim().toLowerCase();
          if (!q) return true;
          const name = `${r.user?.first_name || ''} ${r.user?.last_name || ''}`.toLowerCase();
          const email = (r.user?.email || '').toLowerCase();
          return name.includes(q) || email.includes(q);
        })
    : [];
  const matchesStatusTab = (record, tab) => {
    if (tab === 'all') return true;
    if (tab === 'not_marked') return record.isMissing;
    return !record.isMissing && record.status === tab;
  };
  const filteredRecords = isTeamView
    ? recordsBeforeStatusFilter.filter((r) => matchesStatusTab(r, markStatusFilter))
    : records;

  if (isTeamView) {

    return (
      <div className="attendance-page">
        <div className="page-header">
          <div>
            <div className="page-title">Team Attendance</div>
            <div className="page-subtitle">View attendance across your team</div>
          </div>
        </div>

        <div className="animate-in" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          {[
            { value: 'all', label: 'All' },
            { value: 'working', label: 'Working' },
            { value: 'not_working', label: 'Not Working' },
            { value: 'half_day', label: 'Half Day' },
            { value: 'not_marked', label: 'Not Marked Yet' },
          ].map((tab) => (
            <button
              key={tab.value}
              className={`filter-pill ${markStatusFilter === tab.value ? 'filter-pill--active' : ''}`}
              onClick={() => setMarkStatusFilter(tab.value)}
            >
              {tab.label}
              <span className="filter-pill-count">
                {recordsBeforeStatusFilter.filter((r) => matchesStatusTab(r, tab.value)).length}
              </span>
            </button>
          ))}
        </div>

        <div className="animate-in stagger-1" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          <input
            type="text"
            className="form-input"
            style={{ height: '42px', width: 260 }}
            placeholder="Search employee by name or email…"
            value={teamSearch}
            onChange={(e) => setTeamSearch(e.target.value)}
          />
          <div className="cal-header" style={{ gap: 8, height: '41px', paddingTop: 10 }}>
            <input
              type="date"
              className="form-select"
              style={{ height: '42px', width: 170 }}
              value={teamDate}
              onChange={(e) => setTeamDate(e.target.value)}
              max={today}
            />
          </div>
          <select
            className="form-select"
            style={{ width: 160 }}
            value={companyFilter}
            onChange={(e) => { setCompanyFilter(e.target.value); setTeamFilter('all'); }}
          >
            <option value="all">All Companies</option>
            <option value="Aays">Aays</option>
            <option value="Aidetic">Aidetic</option>
          </select>
          <select
            className="form-select"
            style={{ width: 180 }}
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
          >
            <option value="all">All team members</option>
            {teamUsers.map(([id, name]) => (
              <option key={id} value={id}>{name || 'Unnamed'}</option>
            ))}
          </select>
        </div>

        <div className="card animate-in">
          {filteredRecords.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📋</div>
              <div className="empty-state-title">No attendance records</div>
            </div>
          ) : (
            <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
              <table>
                <thead>
                  <tr>
                    <th>Employee</th><th>Date</th><th>Status</th><th>Work Description</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((r) => {
                    const opt = STATUS_OPTIONS.find((o) => o.value === r.status);
                    return (
                      <tr key={r.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="avatar avatar-sm">
                              {r.user?.first_name?.[0]}{r.user?.last_name?.[0]}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                {r.user?.first_name} {r.user?.last_name}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{r.user?.email}</div>
                            </div>
                          </div>
                        </td>
                        <td><strong>{formatTeamAttendanceDate(r.date, teamDate)}</strong></td>
                        <td>
                          <span className="badge" style={{ background: opt?.bg, color: opt?.color }}>
                            {opt?.icon} {opt?.label}
                          </span>
                        </td>
                        <td style={{ maxWidth: 300 }} className="truncate">{r.work_description || '—'}</td>
                        <td>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openMarkModal(r)}
                          >
                            {r.isMissing ? 'Mark' : 'Edit'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mark/Edit attendance modal (admin/manager backfill for a team member) */}
        {markModal && (
          <div className="modal-overlay" onClick={() => setMarkModal(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Attendance — {markModal.user?.first_name} {markModal.user?.last_name}</h3>
                <button className="btn btn-ghost btn-sm" onClick={() => setMarkModal(null)}>✕</button>
              </div>
              <form onSubmit={submitMarkModal}>
                <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div className="form-group">
                    <label className="form-label">Date</label>
                    <input className="form-input" value={formatTeamAttendanceDate(markModal.date, markModal.date)} disabled />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status *</label>
                    <select
                      className="form-select"
                      value={markModal.status}
                      onChange={(e) => setMarkModal({ ...markModal, status: e.target.value })}
                    >
                      {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.icon} {o.label}</option>)}
                    </select>
                  </div>
                  <div className="form-grid-2col">
                    <div className="form-group">
                      <label className="form-label">Check In</label>
                      <input
                        type="time"
                        className="form-input"
                        value={markModal.checkIn}
                        onChange={(e) => setMarkModal({ ...markModal, checkIn: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Check Out</label>
                      <input
                        type="time"
                        className="form-input"
                        value={markModal.checkOut}
                        onChange={(e) => setMarkModal({ ...markModal, checkOut: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Work Description</label>
                    <textarea
                      className="form-textarea"
                      value={markModal.description}
                      onChange={(e) => setMarkModal({ ...markModal, description: e.target.value })}
                      placeholder="What did they work on?"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setMarkModal(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={markSaving}>{markSaving ? 'Saving…' : 'Save'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="attendance-page">
      <div className="page-header">
        <div>
          <div className="page-title">Attendance</div>
          <div className="page-subtitle">Log your daily work status and activity</div>
        </div>
      </div>

      <div className="attendance-layout">
        {/* Log today */}
        <div className="card attendance-log-card animate-in">
          <div className="card-header" style={{ marginBottom: 0, paddingBottom: 16 }}>
            <h3>
              {todayRecord ? '✏️ Update Today' : '📋 Log Today'}
            </h3>
            <span className="badge badge-violet">
              {new Date(today).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
            </span>
          </div>

          <form onSubmit={handleSave} className="attendance-form">
            {/* Status selector */}
            <div className="form-group">
              <label className="form-label">Work Status *</label>
              <div className="status-options">
                {STATUS_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`status-option ${formStatus === opt.value ? 'status-option--active' : ''}`}
                    style={formStatus === opt.value ? { borderColor: opt.color, background: opt.bg } : {}}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={opt.value}
                      checked={formStatus === opt.value}
                      onChange={() => setFormStatus(opt.value)}
                      className="visually-hidden"
                    />
                    <span>{opt.icon}</span>
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="form-group">
              <label className="form-label">Comments</label>
              <textarea
                className="form-textarea"
                placeholder="e.g. Worked on the analytics dashboard, attended sprint planning, reviewed team PRs…"
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                rows={4}
              />
            </div>

            {/* Check in/out */}
            {/* <div className="checkinout-row">
              <div className="form-group">
                <label className="form-label">Check In</label>
                <input
                  type="time"
                  className="form-input"
                  value={formCheckIn}
                  min={ATTENDANCE_TIME_MIN}
                  max={ATTENDANCE_TIME_MAX}
                  onChange={(e) => setFormCheckIn(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Check Out</label>
                <input
                  type="time"
                  className="form-input"
                  value={formCheckOut}
                  min={ATTENDANCE_TIME_MIN}
                  max={ATTENDANCE_TIME_MAX}
                  onChange={(e) => setFormCheckOut(e.target.value)}
                />
              </div>
            </div> */}

            <button type="submit" className="btn btn-primary" disabled={saving} style={{ width: '100%', marginTop: 4 }}>
              {saving ? 'Saving…' : todayRecord ? 'Update Attendance' : 'Log Attendance'}
            </button>
          </form>
        </div>

        {/* Calendar view */}
        <div className="attendance-right">
          {/* Summary */}
          {summary && (
            <div className="attendance-summary animate-in stagger-1">
              {[
                { label: 'Working', count: summary.working, cls: 'att-working' },
                { label: 'Half Day', count: summary.half_day, cls: 'att-half' },
                { label: 'Not Working', count: summary.not_working, cls: 'att-off' },
              ].map((s) => (
                <div key={s.label} className={`att-sum-card ${s.cls}`}>
                  <span className="att-sum-num">{s.count}</span>
                  <span className="att-sum-label">{s.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Calendar */}
          <div className="card att-calendar animate-in stagger-2">
            <div className="cal-header">
              <button className="btn btn-ghost btn-sm" onClick={prevMonth}>‹</button>
              <span className="cal-month">{monthName}</span>
              <button className="btn btn-ghost btn-sm" onClick={nextMonth}>›</button>
            </div>
            <div className="cal-grid">
              {['Su','Mo','Tu','We','Th','Fr','Sa'].map((d) => (
                <div key={d} className="cal-day-name">{d}</div>
              ))}
              {calCells.map((cell, i) => {
                if (!cell) return <div key={`empty-${i}`} />;
                const isToday = cell.dateStr === today;
                const status = cell.record?.status;
                const leave = cell.leave;
                const holiday = cell.holiday;
                const isWeekend = i % 7 === 0 || i % 7 === 6;
                // Priority: Holiday > Weekend > Leave > Attendance. A multi-day
                // leave request only consumes working days, so a Sat/Sun inside
                // its date range should read as a weekend, not as leave taken.
                let cellType = '';
                let tooltip = '';
                let tagText = '';
                let style = {};
                if (holiday) {
                  cellType = 'holiday';
                  tooltip = `Holiday: ${holiday.name}${holiday.description ? ' - ' + holiday.description : ''}`;
                  tagText = holiday.name;
                  style = { background: '#F0F5FF', color: '#2563eb', border: '1.5px solid #2563eb' };
                } else if (isWeekend) {
                  cellType = 'weekend';
                  tooltip = 'Weekend';
                  tagText = 'WEEKEND';
                  style = { background: '#F3F4F6', color: '#6B7280' };
                } else if (leave) {
                  cellType = 'leave';
                  tooltip = `Leave: ${leave.leave_type.charAt(0).toUpperCase() + leave.leave_type.slice(1)}${leave.reason ? ' - ' + leave.reason : ''}`;
                  tagText = 'LEAVE';
                  style = { background: '#FEF9C3', color: '#B45309', border: '1.5px solid #F59E0B' };
                } else if (status) {
                  cellType = status;
                  tooltip = cell.record?.work_description || '';
                  const statusTag = {
                    working: 'WORKING',
                    half_day: 'HALF DAY',
                    not_working: 'OFF',
                  };
                  tagText = statusTag[status] || 'STATUS';
                  style = statusStyle(status);
                }
                return (
                  <div
                    key={cell.dateStr}
                    className={`cal-day ${isToday ? 'cal-day--today' : ''} cal-day--${cellType}`}
                    title={tooltip}
                    style={{...style, position:'relative'}}>
                    <span>{cell.day}</span>
                    {tagText && (
                      <span className={`cal-day-tag ${cellType === 'holiday' ? 'cal-day-tag--holiday' : ''}`}>
                        {tagText}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="cal-legend" style={{display:'flex',gap:16,marginTop:8}}>
              {STATUS_OPTIONS.map((o) => (
                <span key={o.value} className="cal-legend-item"><span className="cal-legend-dot" style={{ background: o.color }} />{o.label}</span>
              ))}
              <span className="cal-legend-item"><span className="cal-legend-dot" style={{ background: '#F59E0B' }} />Leave</span>
              <span className="cal-legend-item"><span className="cal-legend-dot" style={{ background: '#2563eb' }} />Holiday</span>
              <span className="cal-legend-item"><span className="cal-legend-dot" style={{ background: '#6B7280' }} />Weekend</span>
            </div>
          </div>
        </div>
      </div>

      {/* History table */}
      <div className="card animate-in stagger-3" style={{ marginTop: 24 }}>
        <div className="card-header">
          <h3>Attendance History</h3>
        </div>
        {records.length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">📋</div><div className="empty-state-title">No records yet</div></div>
        ) : (
          <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Status</th><th>Comments</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => {
                  const opt = STATUS_OPTIONS.find((o) => o.value === r.status);
                  return (
                    <tr key={r.id}>
                      <td><strong>{getDayLabel(r.date)}</strong></td>
                      <td>
                        <span className="badge" style={{ background: opt?.bg, color: opt?.color }}>
                          {opt?.icon} {opt?.label}
                        </span>
                      </td>
                      <td style={{ maxWidth: 300 }} className="truncate">{r.work_description || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
